import React from 'react'
import {View} from 'react-native'
import MarkdownIt from 'markdown-it'

type Token = ReturnType<MarkdownIt['parse']>[number]

import {useSession} from '#/state/session'
import {LoggedOutPage} from '#/view/shell/LoggedOutPage'
import {atoms as a, useTheme} from '#/alf'
import * as Layout from '#/components/Layout'
import {InlineLinkText} from '#/components/Link'
import {H2, P, Text} from '#/components/Typography'

const md = new MarkdownIt({linkify: true})

export function PolicyDocument({
  title,
  lastUpdated,
  body,
}: {
  title: string
  lastUpdated?: string
  body: string
}) {
  const t = useTheme()
  const {hasSession} = useSession()
  const tokens = React.useMemo(() => md.parse(body, {}), [body])

  const inner = (
    <View style={[a.px_xl, a.py_lg, a.gap_md]}>
      <Text style={[a.text_2xl, a.font_bold, t.atoms.text]}>{title}</Text>
      {lastUpdated ? (
        <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
          Last updated: {lastUpdated}
        </Text>
      ) : null}
      <View style={[a.gap_md, a.pt_sm]}>{renderBlocks(tokens)}</View>
    </View>
  )

  // Logged-out web renders inside the landing shell (its own fixed header).
  // LoggedOutPage makes the content flow with the footer at the true end of
  // the page. Logged-in users get the standard screen chrome + scroll.
  if (!hasSession) {
    return (
      <LoggedOutPage>
        <Layout.Center>{inner}</Layout.Center>
      </LoggedOutPage>
    )
  }

  return (
    <Layout.Screen>
      <Layout.Header.Outer>
        <Layout.Header.BackButton />
        <Layout.Header.Content>
          <Layout.Header.TitleText>{title}</Layout.Header.TitleText>
        </Layout.Header.Content>
        <Layout.Header.Slot />
      </Layout.Header.Outer>
      <Layout.Content>{inner}</Layout.Content>
    </Layout.Screen>
  )
}

/**
 * Walk the flat markdown-it block-token stream and render each block. Lists are
 * handled by consuming their nested open/close ranges. Inline content (bold,
 * links, plain text) is rendered from the `inline` token's `children`.
 */
function renderBlocks(tokens: Token[]): React.ReactNode[] {
  const out: React.ReactNode[] = []
  let i = 0
  let key = 0

  while (i < tokens.length) {
    const tok = tokens[i]

    if (tok.type === 'heading_open') {
      const inline = tokens[i + 1]
      out.push(<H2 key={key++}>{renderInline(inline?.children ?? [])}</H2>)
      i += 3 // heading_open, inline, heading_close
      continue
    }

    if (tok.type === 'paragraph_open') {
      const inline = tokens[i + 1]
      out.push(<P key={key++}>{renderInline(inline?.children ?? [])}</P>)
      i += 3 // paragraph_open, inline, paragraph_close
      continue
    }

    if (tok.type === 'bullet_list_open' || tok.type === 'ordered_list_open') {
      const ordered = tok.type === 'ordered_list_open'
      const [items, next] = collectListItems(tokens, i)
      out.push(
        <View key={key++} style={[a.gap_xs, a.pl_sm]}>
          {items.map((itemTokens, idx) => (
            <View key={idx} style={[a.flex_row, a.gap_sm]}>
              <Text style={a.font_bold}>{ordered ? `${idx + 1}.` : '•'}</Text>
              <View style={a.flex_1}>{renderBlocks(itemTokens)}</View>
            </View>
          ))}
        </View>,
      )
      i = next
      continue
    }

    // Unhandled block token (hr, etc.) - skip
    i += 1
  }

  return out
}

/**
 * Given the index of a list_open token, return the token ranges for each
 * list_item's inner content, and the index just past the matching list_close.
 */
function collectListItems(
  tokens: Token[],
  openIndex: number,
): [Token[][], number] {
  const items: Token[][] = []
  let depth = 0
  let i = openIndex
  let current: Token[] | null = null

  for (; i < tokens.length; i++) {
    const tok = tokens[i]
    if (tok.type === 'bullet_list_open' || tok.type === 'ordered_list_open') {
      depth++
      if (depth > 1 && current) current.push(tok)
      continue
    }
    if (tok.type === 'bullet_list_close' || tok.type === 'ordered_list_close') {
      depth--
      if (depth === 0) {
        i++
        break
      }
      if (current) current.push(tok)
      continue
    }
    if (depth === 1 && tok.type === 'list_item_open') {
      current = []
      continue
    }
    if (depth === 1 && tok.type === 'list_item_close') {
      if (current) items.push(current)
      current = null
      continue
    }
    if (current) current.push(tok)
  }

  return [items, i]
}

/**
 * Render an inline token's children (text, strong/bold, links) as a run of
 * Text/InlineLinkText nodes.
 */
function renderInline(children: Token[]): React.ReactNode[] {
  const out: React.ReactNode[] = []
  let key = 0
  let bold = 0
  let linkHref: string | null = null
  let linkBuffer: React.ReactNode[] = []

  const pushText = (content: string) => {
    const node = (
      <Text key={key++} style={bold > 0 ? a.font_bold : undefined}>
        {content}
      </Text>
    )
    if (linkHref !== null) {
      linkBuffer.push(node)
    } else {
      out.push(node)
    }
  }

  for (const tok of children) {
    switch (tok.type) {
      case 'text':
        pushText(tok.content)
        break
      case 'softbreak':
      case 'hardbreak':
        pushText(' ')
        break
      case 'strong_open':
        bold++
        break
      case 'strong_close':
        bold = Math.max(0, bold - 1)
        break
      case 'em_open':
      case 'em_close':
        break
      case 'link_open':
        linkHref = tok.attrGet('href') ?? '#'
        linkBuffer = []
        break
      case 'link_close': {
        const href = linkHref ?? '#'
        out.push(
          <InlineLinkText key={key++} to={href} label={href}>
            {linkBuffer}
          </InlineLinkText>,
        )
        linkHref = null
        linkBuffer = []
        break
      }
      default:
        if (tok.content) pushText(tok.content)
        break
    }
  }

  return out
}
